import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Zap, ShieldCheck, RefreshCw,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow, SectionTitle, PrimaryButton, SecondaryButton, GhostLink } from "@/components/site/Bits";
import { Logo } from "@/components/site/Logo";
import { KeyFigures } from "@/components/site/KeyFigures";
import { Ecosystem } from "@/components/site/Ecosystem";
import { useI18n } from "@/i18n/I18nContext";

export default function Home() {
  const { t } = useI18n();
  return (
    <div>
      <section className="relative overflow-hidden grid-bg" data-testid="home-hero">
        <div className="aura w-[600px] h-[600px] bg-blue-700/25 -top-40 -left-32" />
        <div className="aura w-[500px] h-[500px] bg-violet-700/25 top-10 right-0" />
        <div className="aura w-[400px] h-[400px] bg-cyan-600/20 bottom-0 left-1/3" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 glass rounded-full px-4 py-1.5">
              <Zap className="w-3.5 h-3.5" /> {t("home.badge")}
            </span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-8 font-display font-black tracking-tighter text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
            <span className="text-white">{t("home.heroLine1")}</span><br />
            <span className="text-gradient">{t("home.heroLine2")}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-7 text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            {t("home.heroSub")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <PrimaryButton to="/nfc" testid="home-discover-btn">{t("buttons.discover")}</PrimaryButton>
            <SecondaryButton to="/contact" testid="home-contact-btn">{t("buttons.contact")}</SecondaryButton>
          </motion.div>
        </div>
      </section>

      <Ecosystem />

      <section className="relative overflow-hidden py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>{t("home.conceptEyebrow")}</Eyebrow>
            <SectionTitle>{t("home.conceptTitle")}</SectionTitle>
            <p className="mt-6 text-zinc-400 leading-relaxed">{t("home.conceptText")}</p>
            <div className="mt-8 space-y-4">
              {[
                { icon: RefreshCw, k: "updated" }, { icon: ShieldCheck, k: "nolost" }, { icon: Zap, k: "fast" },
              ].map((x) => (
                <div key={x.k} className="flex gap-4">
                  <span className="w-10 h-10 rounded-xl glass flex items-center justify-center shrink-0"><x.icon className="w-5 h-5 text-cyan-400" /></span>
                  <div><p className="text-white font-semibold">{t(`home.b_${x.k}_t`)}</p><p className="text-sm text-zinc-400">{t(`home.b_${x.k}_d`)}</p></div>
                </div>
              ))}
            </div>
            <div className="mt-8"><GhostLink to="/fonctionnement" testid="home-howitworks-link">{t("home.seeHow")}</GhostLink></div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="aura w-[300px] h-[300px] bg-cyan-600/30 top-10 -right-10" />
              <img src="https://images.unsplash.com/photo-1509017174183-0b7e0278f1ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="NFC" className="relative rounded-3xl border border-white/10 w-full object-cover aspect-[4/5] shadow-2xl" />
            </div>
          </Reveal>
        </div>
      </section>

      <KeyFigures />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="glass rounded-[2.5rem] overflow-hidden">
          <div className="p-10 sm:p-14 flex flex-col justify-center">
            <Eyebrow>{t("home.foundersEyebrow")}</Eyebrow>
            <SectionTitle className="text-3xl md:text-4xl">{t("home.foundersTitle")}</SectionTitle>
            <p className="mt-6 text-zinc-400 leading-relaxed">{t("home.foundersText")}</p>
            <div className="mt-8"><GhostLink to="/a-propos" testid="home-about-link">{t("home.discoverStory")}</GhostLink></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-gradient p-12 sm:p-16 text-center">
            <div className="relative">
              <Logo size="text-3xl" />
              <h2 className="mt-6 font-display font-bold text-3xl md:text-4xl text-white max-w-2xl mx-auto">{t("home.ctaTitle")}</h2>
              <p className="mt-4 text-white/80 max-w-xl mx-auto">{t("home.ctaText")}</p>
              <div className="mt-8 flex justify-center">
                <Link to="/contact" data-testid="home-cta-project"
                  className="inline-flex items-center gap-2 bg-[#0a0a12] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#141420] transition-colors duration-300">
                  {t("buttons.requestProject")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
